// frontend/src/components/cadetes/MarcarPresentesSheet.tsx
// Confirmación del marcado de presentes en bloque: todos los pendientes
// arrancan tildados; se destilda a quien debe quedar pendiente. Los cadetes
// con licencia vigente se listan aparte y no se marcan (ya figuran en el Parte
// por su licencia).

import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { AlertTriangle, Check, CheckCheck, RefreshCw, Stethoscope } from 'lucide-react';
import type { Cadete } from '../../lib/api';
import { BottomSheet } from '../ui/bottom-sheet';
import { AnimatedNumber } from '../ui/AnimatedNumber';
import { getArgentinaDateString } from '../../lib/utils';
import { SPRING_POP } from '../../lib/motion';

interface MarcarPresentesSheetProps {
  open: boolean;
  onClose: () => void;
  fecha: string;
  compania?: string;
  pendientes: Cadete[];
  conLicencia: { cadete: Cadete; codigo: string }[];
  onConfirmar: (ids: number[]) => Promise<void>;
}

const fechaLarga = (f: string) =>
  new Date(`${f}T00:00:00`).toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });

export function MarcarPresentesSheet({
  open, onClose, fecha, compania, pendientes, conLicencia, onConfirmar,
}: MarcarPresentesSheetProps) {
  // Ids que quedan pendientes a propósito (destildados)
  const [excluidos, setExcluidos] = useState<Set<number>>(new Set());
  const [guardando, setGuardando] = useState(false);

  // Cada apertura arranca con todos tildados
  useEffect(() => { if (open) setExcluidos(new Set()); }, [open]);

  const marcados = useMemo(() => pendientes.filter((c) => !excluidos.has(c.id)), [pendientes, excluidos]);
  const esHoy = fecha === getArgentinaDateString();

  const alternar = (id: number) => setExcluidos((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const confirmar = async () => {
    if (marcados.length === 0 || guardando) return;
    setGuardando(true);
    try {
      await onConfirmar(marcados.map((c) => c.id));
      onClose();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={() => { if (!guardando) onClose(); }}
      title="Marcar presentes"
      subtitle={`Jornada ${fechaLarga(fecha)}${compania ? ` · ${compania}` : ''}`}
      icon={<CheckCheck size={18} strokeWidth={2} />}
      detent="large"
      footer={(
        <div className="flex flex-col gap-3">
          <button
            id="marcar-presentes-confirmar"
            type="button"
            onClick={confirmar}
            disabled={marcados.length === 0 || guardando}
            className="w-full h-12 rounded-[14px] text-[15px] font-semibold text-white flex items-center justify-center gap-2 tap-active disabled:opacity-50"
            style={{ backgroundColor: '#1a6f3c' }}
          >
            {guardando ? <RefreshCw size={18} className="animate-spin-slow" /> : <CheckCheck size={18} strokeWidth={2.2} />}
            {marcados.length === 0
              ? 'No hay cadetes tildados'
              : <>Marcar <AnimatedNumber value={marcados.length} /> presente{marcados.length === 1 ? '' : 's'}</>}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            className="w-full h-12 rounded-[14px] text-[15px] font-medium tap-active disabled:opacity-50"
            style={{ backgroundColor: 'rgba(60,60,67,0.08)', color: '#43474f' }}
          >
            Cancelar
          </button>
        </div>
      )}
    >
      <div className="px-4 pt-3 pb-4 space-y-3">
        {!esHoy && (
          <div className="flex items-start gap-2.5 rounded-[14px] border p-3" style={{ backgroundColor: '#fff7e0', borderColor: '#fed65b' }}>
            <AlertTriangle size={18} strokeWidth={2} className="shrink-0 mt-0.5" style={{ color: '#9a3412' }} />
            <p className="text-[13px] text-[#43474f] leading-snug">
              Está marcando el <b className="text-[#001e40]">{fechaLarga(fecha)}</b>, no la jornada de hoy.
            </p>
          </div>
        )}

        <p className="text-[13px] text-[#43474f] leading-snug">
          Todos los pendientes quedan <b className="text-[#1a6f3c]">presentes</b>. Destilde a quien deba quedar pendiente.
        </p>

        {/* Todos / Ninguno + contador */}
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setExcluidos(new Set())}
            className="h-9 px-3.5 rounded-full border text-[13px] font-semibold tap-active"
            style={{ borderColor: '#c3c6d1', color: '#001e40', backgroundColor: 'white' }}>
            Todos
          </button>
          <button type="button" onClick={() => setExcluidos(new Set(pendientes.map((c) => c.id)))}
            className="h-9 px-3.5 rounded-full border text-[13px] font-semibold tap-active"
            style={{ borderColor: '#c3c6d1', color: '#001e40', backgroundColor: 'white' }}>
            Ninguno
          </button>
          <span id="marcar-presentes-contador" className="ml-auto text-[13px] font-semibold text-[#43474f]">
            <AnimatedNumber value={marcados.length} /> de {pendientes.length}
          </span>
        </div>

        {/* Lista: toda la fila es el toque */}
        <ul className="bg-white rounded-[16px] overflow-hidden divide-y divide-[#eae8e7]" style={{ boxShadow: 'var(--shadow-ios-card)' }}>
          {pendientes.map((c) => {
            const tildado = !excluidos.has(c.id);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={tildado}
                  onClick={() => alternar(c.id)}
                  data-cadete={c.id}
                  className="w-full min-h-[52px] flex items-center gap-3 px-4 py-2.5 text-left active:bg-[#f6f3f2]"
                >
                  <m.span
                    className="w-6 h-6 rounded-[7px] border-2 flex items-center justify-center shrink-0"
                    animate={tildado
                      ? { backgroundColor: '#1a6f3c', borderColor: '#1a6f3c' }
                      : { backgroundColor: '#ffffff', borderColor: '#c3c6d1' }}
                    transition={{ duration: 0.15 }}
                  >
                    <AnimatePresence initial={false}>
                      {tildado && (
                        <m.span key="v" initial={{ scale: 0 }} animate={{ scale: 1, transition: SPRING_POP }} exit={{ scale: 0, transition: { duration: 0.1 } }}>
                          <Check size={15} strokeWidth={3.2} color="white" />
                        </m.span>
                      )}
                    </AnimatePresence>
                  </m.span>
                  <span className={`flex-1 min-w-0 transition-opacity ${tildado ? '' : 'opacity-55'}`}>
                    <span className="block text-[15px] font-semibold text-[#001e40] truncate">
                      {c.apellido}, {c.nombres}
                    </span>
                    {!tildado && <span className="block text-[11.5px] font-semibold text-[#9a3412]">Queda pendiente</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {conLicencia.length > 0 && (
          <section className="space-y-1.5">
            <p className="flex items-center gap-1.5 px-1 text-ios-footnote font-semibold uppercase tracking-wide text-[#8E8E93]">
              <Stethoscope size={14} strokeWidth={2} /> Con licencia (no se marcan) · {conLicencia.length}
            </p>
            <ul className="rounded-[16px] overflow-hidden divide-y divide-[#eae8e7] border border-[#eae8e7]" id="marcar-presentes-licencias">
              {conLicencia.map(({ cadete, codigo }) => (
                <li key={cadete.id} className="flex items-center gap-3 px-4 py-2.5 bg-[#fbf9f8]">
                  <span className="flex-1 min-w-0 text-[14px] text-[#43474f] truncate">{cadete.apellido}, {cadete.nombres}</span>
                  <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase border bg-[#ede7f6] text-[#4527a0] border-[#d1c4e9]">
                    {codigo}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </BottomSheet>
  );
}
